/* TSO Upsells — rule-driven, vehicle-filtered cross-sells.
 *
 * Rules live in the `upsell_rule` metaobject (read by snippets/tso-upsells.liquid
 * into #tso-upsell-rules JSON): "cart HAS source product type but LACKS target
 * product type -> offer products from the target collection". Type matching uses
 * the established convention: product.type | handleize == product_type
 * metaobject handle (same as the gallery grid).
 *
 * Candidates come from ONE fetch per fired rule:
 *   /collections/<target>?<cookie fitment params>&section_id=tso-upsell-cards
 * so S&D resolves "fits the buyer's vehicle" server-side (Ben: non-fitting
 * products are HIDDEN, never badged). No vehicle -> per-mount behavior
 * (data-no-vehicle: nudge | all | hide; default nudge = "select your truck").
 *
 * Custom (color) products inherit the buyer's picked powder colors from the
 * most recent cart line carrying color properties — matched by LABEL against
 * the candidate's own palette (same rule as builder accessory inherit: a color
 * the product doesn't offer doesn't inherit; falls back to its first option).
 * Preview = TSOColorPreview composite; one-click add mirrors the builder
 * accessory line contract (SKU + color labels + _fee_colors + _PreviewImage +
 * Fitment) so the Cart Transform prices the color upcharge.
 * NOTE: _uid/_ptype intentionally omitted pending the transform-contract
 * answer; lines land as standalone cart rows.
 *
 * Re-runs on: DOMContentLoaded, Dawn's cartUpdate pubsub (section re-renders
 * recreate the mounts), vehicle:selection:updated, and after its own adds.
 */
(function () {
  'use strict';
  if (window.__tsoUpsellsLoaded) return; // snippet renders on multiple surfaces
  window.__tsoUpsellsLoaded = true;

  var runSeq = 0;

  function handleize(s) {
    return String(s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function readRules() {
    var el = document.getElementById('tso-upsell-rules');
    if (!el) return [];
    try { return JSON.parse(el.textContent) || []; } catch (e) { return []; }
  }

  function readVehicle() {
    if (window.TSOVehicle) return window.TSOVehicle.get();
    try {
      var m = document.cookie.match(/(?:^|; )vehicle_selection=([^;]*)/);
      return m ? JSON.parse(decodeURIComponent(m[1])) : null;
    } catch (e) { return null; }
  }

  function moneyFormat() {
    var mount = document.querySelector('[data-tso-upsells]');
    return (mount && mount.getAttribute('data-money-format')) || '${{amount}}';
  }

  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return moneyFormat().replace(/\{\{\s*amount[^}]*\}\}/, amount);
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  // most recent cart line carrying powder color properties (cart.js lists
  // newest first) — the colors the buyer just built with
  function inheritedColors(cart) {
    var items = cart.items || [];
    for (var i = 0; i < items.length; i++) {
      var p = items[i].properties || {};
      if (p['Primary Color'] || p['Secondary Color']) {
        return {
          primary: p['Primary Color'] || null,
          secondary: p['Secondary Color'] || null,
          // Cart Transform v17 powder contract: synced powders price by
          // metaobject GID only — carry the source line's GIDs so a re-used
          // powder label doesn't recreate the label-only outage
          primaryGid: p['_Primary Color GID'] || null,
          secondaryGid: p['_Secondary Color GID'] || null
        };
      }
    }
    return null;
  }

  function firedRules(rules, cartTypes, extraTypes) {
    var pool = {};
    cartTypes.concat(extraTypes || []).forEach(function (t) { if (t) pool[t] = 1; });
    var inCart = {};
    cartTypes.forEach(function (t) { if (t) inCart[t] = 1; });
    return rules
      .filter(function (r) {
        if (r.enabled === false || !r.collection_url || !r.target_type) return false;
        if (inCart[r.target_type]) return false; // already own one
        return (r.source || []).some(function (s) { return pool[s]; });
      })
      .sort(function (a, b) { return (b.priority || 0) - (a.priority || 0); });
  }

  function cardsUrl(rule, sel) {
    var u = new URL(rule.collection_url, window.location.origin);
    if (sel && window.tsoBuildFitmentParams) {
      var p = window.tsoBuildFitmentParams(sel);
      if (p) p.forEach(function (v, k) { u.searchParams.append(k, v); });
    }
    u.searchParams.set('section_id', 'tso-upsell-cards');
    return u.pathname + u.search;
  }

  function fetchCards(rule, sel) {
    return fetch(cardsUrl(rule, sel)).then(function (r) {
      if (!r.ok) throw new Error('upsell cards fetch ' + r.status);
      return r.text();
    }).then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      return Array.prototype.slice.call(doc.querySelectorAll('.tso-upsell-card'));
    });
  }

  function paletteFor(card, sel) {
    function parse(selName) {
      var el = card.querySelector('script[' + selName + ']');
      if (!el) return [];
      try { return JSON.parse(el.textContent) || []; } catch (e) { return []; }
    }
    return { pri: parse('data-pri-colors'), sec: parse('data-sec-colors') };
  }

  function pickColor(palette, wantLabel) {
    if (!palette.length) return null;
    if (wantLabel) {
      var want = String(wantLabel).trim().toLowerCase();
      for (var i = 0; i < palette.length; i++) {
        if (String(palette[i].l).trim().toLowerCase() === want) return palette[i];
      }
    }
    return palette[0]; // product doesn't offer the inherited color -> its default
  }

  function cardPriceCents(card, pri, sec) {
    var base = parseInt(card.getAttribute('data-baseprice'), 10) || 0;
    var price = base;
    if (pri) price += (base * (pri.pc || 0)) / 100;
    if (sec) price += (base * (sec.sc || 0)) / 100;
    return Math.round(price);
  }

  function applyColors(card, inherit, rule) {
    var isCustom = card.getAttribute('data-protype') === 'custom';
    var pri = null, sec = null;
    if (isCustom) {
      var pal = paletteFor(card);
      pri = pickColor(pal.pri, inherit && inherit.primary);
      sec = pickColor(pal.sec, inherit && inherit.secondary);
    }
    card.__pri = pri;
    card.__sec = sec;
    // forward the source line's powder GID ONLY when the inherited label was
    // actually used (fallback-to-default picks must stay label-only)
    function usedInherited(picked, wantLabel) {
      return !!(picked && wantLabel && String(picked.l).trim().toLowerCase() === String(wantLabel).trim().toLowerCase());
    }
    card.__priGid = (inherit && usedInherited(pri, inherit.primary) && inherit.primaryGid) || null;
    card.__secGid = (inherit && usedInherited(sec, inherit.secondary) && inherit.secondaryGid) || null;

    var price = cardPriceCents(card, pri, sec);
    var priceEl = card.querySelector('[data-price]');
    // price > 0 guard: a missing/invalid data-baseprice would otherwise render
    // a misleading $0.00 — leave the server-rendered price in that case
    if (priceEl && price > 0) {
      // upgrade rules show the transform-honored discount (struck full price);
      // display-only — the Cart Transform is the pricing authority
      var pct = (rule && rule.mode === 'upgrade' && rule.discount_pct > 0) ? rule.discount_pct : 0;
      if (pct) {
        var discounted = Math.round(price * (100 - pct) / 100);
        priceEl.innerHTML = '<s>' + formatMoney(price) + '</s> ' + formatMoney(discounted);
      } else {
        priceEl.textContent = formatMoney(price);
      }
    }
    if (!isCustom) return;

    var note = card.querySelector('[data-color-note]');
    if (note && (pri || sec)) {
      note.hidden = false;
      note.textContent = [pri && pri.l, sec && sec.l].filter(Boolean).join(' / ');
    }

    var canvas = card.querySelector('.tso-upsell-card__canvas');
    if (canvas && window.TSOColorPreview && pri) {
      window.TSOColorPreview.render(canvas, {
        base: card.getAttribute('data-mainimage'),
        truck: card.getAttribute('data-truckimage'),
        tip: card.getAttribute('data-tipimage'),
        tipPolished: card.getAttribute('data-tippolishedimage'),
        overlay: card.getAttribute('data-overlayimage'),
        overlayPolished: card.getAttribute('data-overlaypolishedimage'),
        screw: card.getAttribute('data-screwimage')
      }, {
        primaryHex: pri && pri.c,
        primaryPolished: !!(pri && pri.p),
        secondaryHex: sec && sec.c,
        secondaryPolished: !!(sec && sec.p)
      }).then(function () {
        canvas.classList.add('is-ready');
        var img = card.querySelector('.tso-upsell-card__img');
        if (img) img.hidden = true;
      }).catch(function () { /* image fallback stays visible */ });
    }
  }

  function fitmentText(sel) {
    if (!sel || !sel.year) return undefined;
    // server-side fitment filter produced this card -> it fits
    return 'This Product Fits Your ' + sel.year + ' ' + sel.make + ' ' + sel.model;
  }

  function refreshDawnCart() {
    try {
      ['cart-items', 'cart-drawer-items'].forEach(function (tag) {
        var el = document.querySelector(tag);
        if (el && typeof el.onCartUpdate === 'function') el.onCartUpdate();
      });
    } catch (e) {}
  }

  function addToCart(card, sel, btn) {
    var isCustom = card.getAttribute('data-protype') === 'custom';
    var pri = card.__pri;
    var sec = card.__sec;
    var rule = card.__rule;
    var replaced = card.__replaceLine; // upgrade mode: the cart line being swapped out
    var props = {};
    var sku = card.getAttribute('data-varsku') || '';
    var qty = 1;

    // never POST a NaN variant id: a product with no available variant would
    // render data-vid="" -> the add would 400 with a confusing message. Fail
    // fast with a clear state instead of a round-trip.
    var vid = parseInt(card.getAttribute('data-vid'), 10);
    if (!(vid > 0)) {
      var badNote = card.querySelector('[data-color-note]');
      if (badNote) { badNote.hidden = false; badNote.textContent = 'Currently unavailable'; }
      btn.disabled = true;
      if (window.tsoReportError) window.tsoReportError('upsell: card has no valid variant id', { handle: card.getAttribute('data-handle') });
      return;
    }

    if (isCustom) {
      sku = sku + ((pri && pri.s) || '') + ((sec && sec.s) || '');
      props['SKU'] = sku;
      props['Primary Color'] = (pri && pri.l) || '';
      props['Secondary Color'] = (sec && sec.l) || '';
      if (card.__priGid) props['_Primary Color GID'] = card.__priGid;
      if (card.__secGid) props['_Secondary Color GID'] = card.__secGid;
      var fees = [];
      [pri, sec].forEach(function (o) {
        if (o && o.f && o.l && fees.indexOf(o.l) === -1) fees.push(o.l);
      });
      if (fees.length) props['_fee_colors'] = fees.join('|');
    }
    var fit = fitmentText(sel);
    if (fit) props['Fitment'] = fit;

    // upgrade contract (docs/CART_TRANSFORM_THEME_CONTRACT.md): the transform
    // validates _upgrade_rule server-side and applies the upgrade price —
    // the properties are a CLAIM, never trusted alone for pricing
    if (rule && rule.mode === 'upgrade' && replaced) {
      props['_upgrade_rule'] = rule.handle;
      props['_upgrade_replaced'] = replaced.handle;
      qty = replaced.quantity || 1;
    }

    btn.disabled = true;
    var originalLabel = btn.textContent;
    btn.textContent = 'Adding…';

    var previewStep = Promise.resolve();
    var canvas = card.querySelector('.tso-upsell-card__canvas.is-ready');
    if (isCustom && canvas && window.tsoUploadPreview) {
      previewStep = window.tsoUploadPreview(canvas, sku + '.jpg').then(function (url) {
        props['_PreviewImage'] = url;
      });
    }

    previewStep.then(function () {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: vid, quantity: qty, properties: props }]
        })
      });
    }).then(function (res) {
      if (!res.ok) return res.json().then(function (j) { throw new Error(j.description || j.message || ('cart add ' + res.status)); });
      // upgrade: remove the replaced line AFTER the add succeeded (add-first
      // ordering — a failed swap leaves both lines rather than neither)
      if (rule && rule.mode === 'upgrade' && replaced && replaced.key) {
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: replaced.key, quantity: 0 })
        }).then(function (rmRes) {
          // add-first ordering means a failed removal leaves BOTH lines rather
          // than neither (safe), but it must not be silent — surface it so the
          // orphaned line is caught instead of quietly double-charging
          if (!rmRes.ok && window.tsoReportError) {
            window.tsoReportError('upsell: upgrade add succeeded but removing the replaced line failed', {
              rule: rule.handle, replaced: replaced.key
            });
          }
          return rmRes;
        });
      }
      return res;
    }).then(function () {
      btn.textContent = 'Added ✓';
      try {
        document.dispatchEvent(new CustomEvent('tso:upsell', { detail: {
          action: 'added', rule: rule && rule.handle, mode: (rule && rule.mode) || 'addon',
          handle: card.getAttribute('data-handle')
        } }));
      } catch (e) {}
      // custom-color-fee's fetch-wrap schedules the fee reconcile itself;
      // refresh the visible cart + re-evaluate rules against the new cart.
      // Second pass covers the drawer's async section re-render replacing
      // our mount after the first pass populated it.
      refreshDawnCart();
      setTimeout(runAll, 400);
      setTimeout(runAll, 1600);
    }).catch(function (err) {
      btn.disabled = false;
      btn.textContent = originalLabel;
      var note = card.querySelector('[data-color-note]');
      if (note) { note.hidden = false; note.textContent = 'Could not add — please try again.'; }
      if (window.tsoReportError) window.tsoReportError('upsell: cart add failed', { error: String(err), handle: card.getAttribute('data-handle') });
    });
  }

  function wireCard(card, sel) {
    var btn = card.querySelector('[data-cta]');
    if (!btn) return;
    var isUpgrade = card.__rule && card.__rule.mode === 'upgrade' && card.__replaceLine;
    if (card.getAttribute('data-single') !== '1') {
      // multi-variant products need real option selection -> go to the PDP
      btn.addEventListener('click', function () {
        var url = card.getAttribute('data-url');
        if (url) window.location.href = url; // guard a section that rendered no url
      });
      return;
    }
    if (isUpgrade) {
      btn.textContent = card.__rule.discount_pct > 0
        ? 'Upgrade & save ' + card.__rule.discount_pct + '%'
        : 'Upgrade';
    }
    btn.addEventListener('click', function () { addToCart(card, sel, btn); });
  }

  // upgrade rules swap out a cart line: the newest line whose product type is
  // one of the rule's sources (its colors also seed the inherit, so the
  // upgrade lands in the same powder the buyer already picked)
  function findReplaceLine(cart, rule) {
    var items = cart.items || [];
    for (var i = 0; i < items.length; i++) {
      if ((rule.source || []).indexOf(handleize(items[i].product_type)) !== -1) return items[i];
    }
    return null;
  }

  function renderNudge(body) {
    var div = document.createElement('div');
    div.className = 'tso-upsell-nudge';
    div.innerHTML = '<p>Select your truck to see parts that fit it.</p>' +
      '<button type="button" class="cpb-btn cpb-btn--primary" data-tso-upsell-nudge>Select your vehicle</button>';
    div.querySelector('[data-tso-upsell-nudge]').addEventListener('click', function () {
      var d = document.getElementById('vs-drawer');
      var o = document.getElementById('vs-overlay');
      if (!d || !o) return;
      d.hidden = false; o.hidden = false;
      requestAnimationFrame(function () { d.classList.add('is-open'); o.classList.add('is-open'); });
    });
    body.appendChild(div);
  }

  function renderMount(mount, cart, rules, sel, seq) {
    var body = mount.querySelector('[data-tso-upsells-body]');
    var headingEl = mount.querySelector('[data-tso-upsells-heading]');
    if (!body) return;

    var cartTypes = (cart.items || []).map(function (it) { return handleize(it.product_type); });
    var extra = (mount.getAttribute('data-extra-types') || '').split(',').map(handleize).filter(Boolean);
    var fired = firedRules(rules, cartTypes, extra);

    if (!fired.length) { mount.hidden = true; body.innerHTML = ''; return; }

    if (!sel || !sel.filter_value) {
      var mode = mount.getAttribute('data-no-vehicle') || 'nudge';
      if (mode === 'hide') { mount.hidden = true; body.innerHTML = ''; return; }
      if (mode === 'nudge') {
        body.innerHTML = '';
        if (headingEl) headingEl.textContent = fired[0].heading || 'Complete your build';
        renderNudge(body);
        mount.hidden = false;
        return;
      }
      // mode 'all' falls through with sel=null -> unfiltered collection
    }

    var excludeHandles = {};
    (cart.items || []).forEach(function (it) { excludeHandles[it.handle] = 1; });
    (mount.getAttribute('data-exclude') || '').split(',').forEach(function (h) { if (h) excludeHandles[h.trim()] = 1; });
    var max = parseInt(mount.getAttribute('data-max'), 10) || 4;
    var inherit = inheritedColors(cart);

    // try fired rules in priority order until one yields cards
    (function tryRule(i) {
      if (i >= fired.length || i >= 3) { mount.hidden = true; body.innerHTML = ''; return; }
      var rule = fired[i];
      var replaceLine = rule.mode === 'upgrade' ? findReplaceLine(cart, rule) : null;
      if (rule.mode === 'upgrade' && !replaceLine) return tryRule(i + 1); // nothing to swap
      fetchCards(rule, sel && sel.filter_value ? sel : null).then(function (cards) {
        if (seq !== runSeq) return; // stale run
        cards = cards.filter(function (c) {
          if (excludeHandles[c.getAttribute('data-handle')]) return false;
          // drop single-variant cards that would add directly but carry no real
          // variant id (unbuyable / no available variant) — never show an
          // add button that can only fail
          if (c.getAttribute('data-single') === '1' && !(parseInt(c.getAttribute('data-vid'), 10) > 0)) return false;
          return true;
        }).slice(0, max);
        if (!cards.length) return tryRule(i + 1);
        body.innerHTML = '';
        if (headingEl) headingEl.textContent = rule.heading || (rule.mode === 'upgrade' ? 'Upgrade your build' : 'Complete your build');
        // upgrades inherit from the line being replaced; add-ons from the
        // newest colored line in the cart
        var cardInherit = inherit;
        if (replaceLine) {
          var rp = replaceLine.properties || {};
          if (rp['Primary Color'] || rp['Secondary Color']) {
            cardInherit = {
              primary: rp['Primary Color'] || null,
              secondary: rp['Secondary Color'] || null,
              primaryGid: rp['_Primary Color GID'] || null,
              secondaryGid: rp['_Secondary Color GID'] || null
            };
          }
        }
        cards.forEach(function (c) {
          c.__rule = rule;
          c.__replaceLine = replaceLine;
          body.appendChild(c);
          applyColors(c, cardInherit, rule);
          wireCard(c, sel);
        });
        mount.hidden = false;
        try {
          document.dispatchEvent(new CustomEvent('tso:upsell', { detail: {
            action: 'shown', rule: rule.handle, mode: rule.mode || 'addon',
            context: mount.getAttribute('data-context'),
            products: cards.map(function (c) { return c.getAttribute('data-handle'); })
          } }));
        } catch (e) {}
      }).catch(function () { tryRule(i + 1); });
    })(0);
  }

  function runAll() {
    var mounts = document.querySelectorAll('[data-tso-upsells]');
    if (!mounts.length) return;
    var rules = readRules();
    if (!rules.length) return;
    var seq = ++runSeq;
    var sel = readVehicle();
    fetchCart().then(function (cart) {
      if (seq !== runSeq) return;
      mounts.forEach(function (m) { renderMount(m, cart, rules, sel, seq); });
    }).catch(function () {});
  }

  function start() {
    runAll();
    document.addEventListener('vehicle:selection:updated', runAll);
    // Dawn re-renders cart sections (recreating our mounts) and publishes
    // cartUpdate via pubsub — re-run when available
    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () { setTimeout(runAll, 250); });
    } else {
      // pubsub not loaded on this template — fall back to watching cart fetches
      var origFetch = window.fetch.bind(window);
      window.fetch = function (input, init) {
        var url = (typeof input === 'string') ? input : (input && input.url) || '';
        var p = origFetch(input, init);
        if (/\/cart\/(add|change|update|clear)(\.js|\.json)?($|\?)/.test(String(url))) {
          p.then(function () { setTimeout(runAll, 400); }).catch(function () {});
        }
        return p;
      };
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
